import { env } from '@/env';
import { useAuthStore } from '@/auth/store';

/** Backend wraps successes in `{ success, statusCode, data, ... }`. We
 * unwrap to `data` and throw [ApiError] on failure. */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized() {
    return this.statusCode === 401;
  }
  get isForbidden() {
    return this.statusCode === 403;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the `Authorization` header (login + refresh paths). */
  skipAuth?: boolean;
  /** Send as multipart/form-data — `body` must be a FormData instance.
   * The browser sets the boundary header automatically; we omit
   * Content-Type so it can. Used by the runtimes upload form. */
  multipart?: boolean;
}

/** Concurrent 401s share a single refresh attempt. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const store = useAuthStore.getState();
    if (!store.refreshToken) return false;
    try {
      const res = await fetch(`${env.apiBaseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: store.refreshToken }),
      });
      if (!res.ok) return false;
      const json = await res.json();
      const data = json?.data;
      if (typeof data?.accessToken !== 'string') return false;
      store.setTokens(data.accessToken, data.refreshToken ?? store.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Allow next failure to retry refresh
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${env.apiBaseUrl}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function rawRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, skipAuth, headers, multipart, ...rest } = opts;
  const finalHeaders: Record<string, string> = {
    accept: 'application/json',
    // Don't set Content-Type for multipart — fetch picks the right
    // multipart/form-data; <boundary> when body is FormData. JSON path
    // gets the JSON content-type as before.
    ...(body !== undefined && !multipart
      ? { 'content-type': 'application/json' }
      : {}),
    ...((headers as Record<string, string>) ?? {}),
  };
  if (!skipAuth) {
    const access = useAuthStore.getState().accessToken;
    if (access) finalHeaders['authorization'] = `Bearer ${access}`;
  }
  const res = await fetch(buildUrl(path, query), {
    ...rest,
    headers: finalHeaders,
    body:
      body === undefined
        ? undefined
        : multipart
          ? (body as BodyInit)
          : JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `Request failed (${res.status}).`;
    let code: string | undefined;
    try {
      const json = await res.json();
      if (typeof json?.message === 'string') message = json.message;
      if (typeof json?.error?.code === 'string') code = json.error.code;
      else if (typeof json?.error === 'string') message = json.error;
    } catch {
      // body wasn't JSON, keep generic message
    }
    throw new ApiError(message, res.status, code);
  }
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  // Most endpoints wrap in `{success, statusCode, data, ...}`. Unwrap when
  // present, otherwise pass through (some legacy endpoints return the body
  // directly).
  return (json?.data ?? json) as T;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthorized && !opts.skipAuth) {
      const ok = await refreshToken();
      if (ok) return rawRequest<T>(path, opts);
      // Refresh failed — log out so the router redirects to /login
      useAuthStore.getState().clear();
    }
    throw err;
  }
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};
